import { ApolloClient, useApolloClient } from '@apollo/client';
import {
  AutoScrollActivator,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  PointerSensorOptions,
  UniqueIdentifier,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Props as DndContextProps } from '@dnd-kit/core/dist/components/DndContext/DndContext';
import { arrayMove } from '@dnd-kit/sortable';
import { isEmpty, isNil } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useEntitySelection } from '@shared/api/providers/entitySelection';
import { Queries } from '@shared/api/queries';
import { FilterSelectionState } from '@shared/components/filter/types';
import { Entity } from '@shared/data/entity/Entity';
import { useMoveWorkflowEntities } from '@shared/scenes/entity/edit/hooks/useMoveWorkflowEntities';
import { useUpdateWorkflowEntities } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntities';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { DragProxy } from './DragProxy';
import { ActiveCard, DraggableContext } from './DraggableContext';

type EntityGroup = {
  entities: Entity[];
  entityVariables: API.ListWorkflowEntitiesQueryVariables;
  groupData: FilterSelectionState;
  groupComponentId: string;
  groupId: UniqueIdentifier;
};

export type EntityGroupState = Record<UniqueIdentifier, EntityGroup>;

interface Props {
  children: ReactNode;
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate: API.ViewTemplateFragment;
  setEntityGroupState: Dispatch<SetStateAction<EntityGroupState>>;
  isDragSortEnabled: boolean;
}

const dndContextConfig: Partial<DndContextProps> = {
  autoScroll: {
    activator: AutoScrollActivator.DraggableRect,
    acceleration: 1,
    layoutShiftCompensation: true,
  },
  collisionDetection: pointerWithin,
  measuring: {
    droppable: {
      strategy: MeasuringStrategy.Always,
      frequency: 10,
    },
  },
};

const pointerSensorConfig: Partial<PointerSensorOptions> = {
  activationConstraint: {
    distance: grid(0.5),
  },
};

interface DraggableState {
  isDragging: boolean;
  activeCards: ActiveCard[];
  activeGroupId?: UniqueIdentifier;
}

const defaultDraggableState: DraggableState = {
  isDragging: false,
  activeCards: [],
};

type AsyncMutation = () => Promise<void>;

export function DraggableContextProvider({
  children,
  viewTemplate,
  workflowTemplate,
  setEntityGroupState,
  isDragSortEnabled,
}: Props) {
  const [draggableState, setDraggableState] = useState<DraggableState>(defaultDraggableState);
  const apolloClient = useApolloClient();
  const workflowTemplateId = workflowTemplate.id;
  const viewTemplateId = viewTemplate.id;
  const updateWorkflowEntities = useUpdateWorkflowEntities({ workflowTemplateId });
  const moveWorkflowEntity = useMoveWorkflowEntities(viewTemplateId);
  const { selectedEntities } = useEntitySelection();
  const [mutations, setMutations] = useState<AsyncMutation[]>([]);
  const mutationLoading = updateWorkflowEntities.loading || moveWorkflowEntity.loading;

  const pointerSensor = useSensor(PointerSensor, pointerSensorConfig);
  const sensors = useSensors(pointerSensor);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      setEntityGroupState(entityGroupState => {
        const isDragging = true;
        const activeGroupId = event.active.data.current?.groupId;
        const activeEntity = event.active.data.current?.entity;
        const activeEntities = getActiveEntities({ activeEntity, selectedEntities });
        const activeCards = activeEntities
          .map(entity => toActiveCard({ entity, entityGroupState, apolloClient }))
          .filter(notNil);
        setDraggableState({ isDragging, activeCards, activeGroupId });
        return entityGroupState;
      });
    },
    [apolloClient, setEntityGroupState, selectedEntities],
  );

  const onDragOver = useCallback(
    (event: DragOverEvent) => {
      const { collisions } = event;
      const overGroup = collisions?.find(
        collision => collision.data?.droppableContainer.data.current.type === 'group',
      );

      setDraggableState(prevDraggableState => {
        const { activeCards, activeGroupId } = prevDraggableState;
        if (!activeCards) {
          return prevDraggableState;
        }

        activeCards.forEach(activeCard => {
          setEntityGroupState(entityGroupState => {
            const activeEntity = activeCard.workflowEntity;
            const oldGroupId = activeCard.groupId;
            const newGroupId = overGroup?.id;
            if (
              isNil(activeEntity) ||
              isNil(oldGroupId) ||
              isNil(newGroupId) ||
              isNil(activeGroupId)
            ) {
              return entityGroupState;
            }

            const newGroup = entityGroupState[newGroupId];

            const groupComponentId = newGroup.groupComponentId;
            const filterType = newGroup.groupData.filter?.type ?? '';
            const currentValues = getCurrentValues({
              entity: activeEntity,
              groupComponentId,
              filterType,
            });
            const updatedValues = getUpdatedValues({
              currentValues,
              oldGroupId,
              newGroupId,
            });

            updateApolloCache({
              apolloClient,
              entity: activeEntity,
              entityGroupState,
              updatedValues,
            });

            return entityGroupState;
          });
        });

        return { ...prevDraggableState, activeGroupId: overGroup?.id };
      });
    },
    [apolloClient, setEntityGroupState],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { over } = event;

      setDraggableState(prevDraggableState => {
        const { activeCards, activeGroupId } = prevDraggableState;
        if (!activeCards || isNil(activeGroupId)) {
          return prevDraggableState;
        }

        const entityUpdates: API.UpdateWorkflowEntity[] = [];

        // loop through active cards and create a mutation to update the group
        // value for each card
        activeCards.forEach(activeCard => {
          setEntityGroupState(entityGroupState => {
            const activeWorkflowEntity = activeCard.workflowEntity;
            const oldGroupId = activeCard.groupId;
            const groupHasChanged = oldGroupId !== activeGroupId;

            if (isNil(activeWorkflowEntity)) {
              return entityGroupState;
            }

            if (groupHasChanged) {
              const activeGroup = entityGroupState[activeGroupId];
              const groupComponentId = activeGroup.groupComponentId;
              const filterType = activeGroup.groupData.filter?.type ?? '';
              const currentValues = getCurrentValues({
                entity: activeWorkflowEntity,
                groupComponentId,
                filterType,
              });

              const updatedValues = getUpdatedValues({
                currentValues,
                oldGroupId,
                newGroupId: activeGroupId,
              });

              const groupItemValues = updatedValues.map(item =>
                getGroupItemValue(item, filterType),
              );
              const updatedFields = {
                [groupComponentId]: groupItemValues,
              };
              entityUpdates.push({
                id: activeWorkflowEntity.id,
                fields: updatedFields,
              });
            }

            return entityGroupState;
          });
        });
        if (entityUpdates.length > 0) {
          const updateEntites = async () => {
            updateWorkflowEntities.mutate(entityUpdates);
          };
          setMutations(prev => [...prev, updateEntites]);
        }

        // reorder the cards in the new group
        setEntityGroupState(entityGroupState => {
          const overEntityId = over?.data.current?.entityId;
          const activeGroup = entityGroupState[activeGroupId];
          const activeWorkflowEntities = activeCards.map(card => card.workflowEntity);
          const activeEntities = activeCards.map(card => card.entity);

          const groupCache = apolloClient.readQuery({
            query: Queries.workflow.listWorkflowEntities,
            variables: activeGroup.entityVariables,
          });
          const cachedWorkflowEntities: API.WorkflowEntity[] = groupCache.list.entities;
          const updatedWorkflowEntities = getUpdatedEntityList({
            cachedEntities: cachedWorkflowEntities,
            activeEntities: activeWorkflowEntities,
            overEntityId,
          });

          const group = entityGroupState[activeGroupId];
          const cachedEntities = group.entities;
          const updatedEntities = getUpdatedEntityList({
            cachedEntities,
            activeEntities,
            overEntityId,
          });

          const firstActiveCard = activeCards[0];
          const lastActiveCard = activeCards[activeCards.length - 1];
          const newStartIndex = updatedEntities.findIndex(
            item => item.id === firstActiveCard.entity.id,
          );
          const newEndIndex = updatedEntities.findIndex(
            item => item.id === lastActiveCard.entity.id,
          );
          const newPrevEntity = updatedEntities[newStartIndex - 1];
          const newNextEntity = updatedEntities[newEndIndex + 1];

          const hasNeighbor = newPrevEntity || newNextEntity;
          const prevNeighborChanged = newPrevEntity?.id !== firstActiveCard.prevEntity?.id;
          const nextNeighborChanged = newNextEntity?.id !== firstActiveCard.nextEntity?.id;
          const positionHasChanged = prevNeighborChanged || nextNeighborChanged;

          if (hasNeighbor && positionHasChanged && isDragSortEnabled) {
            const moveEntities = async () => {
              const entityIds = activeCards.map(card => card.workflowEntity.id);
              await moveWorkflowEntity.mutate({
                entityIds,
                afterEntityIdDescending: newPrevEntity?.id,
                beforeEntityIdDescending: newNextEntity?.id,
              });
            };
            setMutations(prev => [...prev, moveEntities]);
          }

          if (isDragSortEnabled) {
            apolloClient.writeQuery({
              query: Queries.workflow.listWorkflowEntities,
              variables: activeGroup.entityVariables,
              data: {
                ...groupCache,
                list: {
                  ...groupCache.list,
                  entities: updatedWorkflowEntities,
                },
              },
            });
            return {
              ...entityGroupState,
              [activeGroupId]: {
                ...activeGroup,
                entities: updatedEntities,
              },
            };
          } else {
            return entityGroupState;
          }
        });

        // reset draggable state at end of drag
        return defaultDraggableState;
      });
    },
    [
      apolloClient,
      updateWorkflowEntities,
      moveWorkflowEntity,
      setEntityGroupState,
      isDragSortEnabled,
    ],
  );

  const onDragCancel = useCallback(() => {
    setDraggableState(defaultDraggableState);
  }, []);

  // execute mutations after the drag has ended, one at a time
  useEffect(() => {
    if (isEmpty(mutations) || mutationLoading) {
      return;
    }
    const [mutation, ...rest] = mutations;
    mutation();
    setMutations(rest);
  }, [mutations, mutationLoading]);

  const { isDragging, activeCards, activeGroupId } = draggableState;
  const contextValue = useMemo(
    () => ({
      isDragEnabled: true,
      isDragSortEnabled,
      isDragging,
      activeCards,
      activeGroupId,
    }),
    [activeCards, activeGroupId, isDragSortEnabled, isDragging],
  );

  return (
    <DndContext
      {...{ sensors, onDragStart, onDragOver, onDragEnd, onDragCancel }}
      {...dndContextConfig}
    >
      <DraggableContext.Provider value={contextValue}>{children}</DraggableContext.Provider>
      <DragOverlay>
        {activeCards && <DragProxy {...{ activeCards, viewTemplate, workflowTemplate }} />}
      </DragOverlay>
    </DndContext>
  );
}

function updateApolloCache({
  apolloClient,
  entity,
  entityGroupState,
  updatedValues,
}: {
  apolloClient: ApolloClient<object>;
  entity: API.WorkflowEntity;
  entityGroupState: EntityGroupState;
  updatedValues: UniqueIdentifier[];
}) {
  Object.values(entityGroupState).forEach(group => {
    const groupCache = apolloClient.readQuery({
      query: Queries.workflow.listWorkflowEntities,
      variables: group.entityVariables,
    });
    if (!groupCache) {
      return;
    }

    const hasEntity = groupCache.list.entities.some(
      (item: API.WorkflowEntity) => item.id === entity.id,
    );
    const shouldHaveEntity =
      updatedValues.includes(group.groupId) ||
      (group.groupId === 'empty' && isEmpty(updatedValues));

    const groupComponentId = group.groupComponentId;
    const filterType = group.groupData.filter?.type ?? '';
    const groupItemValues = updatedValues.map(item => getGroupItemValue(item, filterType));

    const updates = {
      [groupComponentId]: groupItemValues,
    };
    const updatedEntity = {
      ...entity,
      fields: { ...entity.fields, ...updates },
    };

    if (shouldHaveEntity && hasEntity) {
      apolloClient.writeQuery({
        query: Queries.workflow.listWorkflowEntities,
        variables: group.entityVariables,
        data: {
          ...groupCache,
          list: {
            ...groupCache.list,
            entities: groupCache.list.entities.map((item: API.WorkflowEntity) => {
              if (item.id === entity.id) {
                return updatedEntity;
              } else {
                return item;
              }
            }),
          },
        },
      });
    } else if (shouldHaveEntity && !hasEntity) {
      // if we somehow have more items in the cache than the totalResults, use
      // the larger of the two numbers, so the rendered card count matches the
      // number of cards shown in the column
      const totalResults = Math.max(
        groupCache.list.totalResults + 1,
        groupCache.list.entities.length + 1,
      );
      apolloClient.writeQuery({
        query: Queries.workflow.listWorkflowEntities,
        variables: group.entityVariables,
        data: {
          ...groupCache,
          list: {
            ...groupCache.list,
            totalResults,
            entities: [updatedEntity, ...groupCache.list.entities],
          },
        },
      });
    } else if (!shouldHaveEntity && hasEntity) {
      const totalResults = Math.max(0, groupCache.list.totalResults - 1);
      apolloClient.writeQuery({
        query: Queries.workflow.listWorkflowEntities,
        variables: group.entityVariables,
        data: {
          ...groupCache,
          list: {
            ...groupCache.list,
            totalResults,
            entities: groupCache.list.entities.filter(
              (item: API.WorkflowEntity) => item.id !== entity.id,
            ),
          },
        },
      });
    }
  });
}

function getCurrentValues({
  entity,
  groupComponentId,
  filterType,
}: {
  entity: API.WorkflowEntity;
  groupComponentId: string;
  filterType: string;
}): UniqueIdentifier[] {
  const fieldValues = entity.fields?.[groupComponentId] ?? [];
  if (filterType === 'RELATED_CARD') {
    return fieldValues.map(({ id }) => id);
  } else {
    return fieldValues;
  }
}

function getUpdatedValues({
  currentValues,
  oldGroupId,
  newGroupId,
}: {
  currentValues: UniqueIdentifier[];
  oldGroupId: UniqueIdentifier;
  newGroupId: UniqueIdentifier;
}): UniqueIdentifier[] {
  if (newGroupId === 'empty') {
    return [];
  } else if (oldGroupId === 'empty') {
    return [newGroupId];
  } else if (oldGroupId === newGroupId) {
    return currentValues;
  } else {
    return [
      ...currentValues.filter(item => item !== oldGroupId && item !== newGroupId),
      newGroupId,
    ];
  }
}

function getGroupItemValue(
  value: UniqueIdentifier,
  filterType: string,
): UniqueIdentifier | Record<string, UniqueIdentifier> {
  if (filterType === 'RELATED_CARD') {
    return { id: value };
  } else {
    return value;
  }
}

export function getDraggableId({
  entityId,
  groupId,
  activeGroupId,
  activeCards,
}: {
  entityId: string;
  groupId: UniqueIdentifier;
  activeGroupId?: UniqueIdentifier;
  activeCards?: ActiveCard[];
}) {
  const firstActiveCard = activeCards?.[0];
  const isActiveGroup = activeGroupId === groupId;
  const isFirstActiveCard = firstActiveCard && firstActiveCard.entity.id === entityId;
  if (activeCards && isActiveGroup && isFirstActiveCard) {
    return firstActiveCard.id;
  } else {
    return `${groupId}-${entityId}`;
  }
}

function toActiveCard({
  entity,
  entityGroupState,
  apolloClient,
}: {
  entity: Entity;
  entityGroupState: EntityGroupState;
  apolloClient: ApolloClient<object>;
}): ActiveCard | undefined {
  const group = Object.values(entityGroupState).find(g =>
    g.entities.some(item => item.id === entity.id),
  );
  if (!group) {
    return undefined;
  }

  const groupCache = apolloClient.readQuery({
    query: Queries.workflow.listWorkflowEntities,
    variables: group.entityVariables,
  });
  const index = groupCache.list.entities.findIndex(
    (item: API.WorkflowEntity) => item.id === entity.id,
  );
  const workflowEntity = groupCache.list.entities[index];
  const prevEntity = groupCache.list.entities[index - 1];
  const nextEntity = groupCache.list.entities[index + 1];
  return {
    id: getDraggableId({ entityId: entity.id, groupId: group.groupId }),
    entity,
    workflowEntity,
    groupId: group.groupId,
    prevEntity,
    nextEntity,
  };
}

function getActiveEntities({
  activeEntity,
  selectedEntities,
}: {
  activeEntity: Entity;
  selectedEntities: Entity[];
}) {
  // If entity selection is active, and the event.active card is in the
  // selection, build the array of active cards from the selection. If
  // not, just use the event.active card.
  const isSelected = selectedEntities.some(entity => entity.id === activeEntity.id);
  if (!isSelected) {
    return [activeEntity];
  } else {
    return [activeEntity, ...selectedEntities.filter(entity => entity.id !== activeEntity.id)];
  }
}

function getUpdatedEntityList<T extends { id: string }>({
  cachedEntities,
  activeEntities,
  overEntityId,
}: {
  cachedEntities: T[];
  activeEntities: T[];
  overEntityId: string;
}): T[] {
  const [firstActiveEntity, ...otherActiveEntities] = activeEntities;
  const activeIndex = cachedEntities.findIndex(item => item.id === firstActiveEntity.id);
  const overIndex = cachedEntities.findIndex(item => item.id === overEntityId);
  const updatedEntities = arrayMove(cachedEntities, activeIndex, overIndex);
  return updatedEntities
    .filter(notNil)
    .filter(item => !otherActiveEntities.some(activeItem => activeItem.id === item.id))
    .reduce((memo: T[], item) => {
      if (item.id === firstActiveEntity.id) {
        return [...memo, firstActiveEntity, ...otherActiveEntities];
      } else {
        return [...memo, item];
      }
    }, []);
}
